<template>
	<div>
		<v-snackbar v-model="snackbar.show" top :color="snackbar.color" :timeout="snackbar.timeout">
			{{ snackbar.message }}
			<v-btn :color="snackbar.buttonColor" flat @click="snackbar.show = false"> Close </v-btn>
		</v-snackbar>

		<div v-if="goalLoadError" v-html="goalLoadError"></div>
		<v-alert v-else-if="goalDeleted" :value="true" color="info" outline class="text-xs-left">This goal has been deleted</v-alert>
		<div v-else-if="goalLoaded">
			<v-container grid-list-xl fluid>
				<v-layout row wrap>
					<v-flex md6>
						<v-card class="mb-4">
							<v-toolbar flat dense :dark="goal.complete" :class="{ 'success': goal.complete }">
								<v-toolbar-title class="subheading">Goal Detail</v-toolbar-title>
								<v-spacer></v-spacer>
								<v-chip v-if="!goal.complete" class="v-chip--x-small">Pending Completion</v-chip>
								<v-chip v-else outline color="white" class="v-chip--x-small" text-color="white">Goal Complete </v-chip>
							</v-toolbar>
							<v-divider></v-divider>

							<v-card-text>
								<v-list class="goal-detail" two-line>
									
									<v-list-tile inactive>
										<v-list-tile-action>
											<v-icon color="indigo">mdi-bullseye-arrow</v-icon>
										</v-list-tile-action>

										<v-list-tile-content>
											<div style="display: flex; width: 100%">
												<div style="flex: 1">
													<div>{{ goal.goal }}</div>
													<v-list-tile-sub-title>Goal</v-list-tile-sub-title>
													<v-chip class="v-chip--x-small white--text" :color="getCategoryChipColor(goal.category)">{{ getCategoryChipText(goal.category, true) }}</v-chip>							
												</div>

												<div class="ml-2">
													<goal-progress :goal="goal" />
												</div>
											</div>
										</v-list-tile-content>
									</v-list-tile>

									<v-list-tile inactive v-if="goal.description">
										<v-list-tile-action>
											<v-icon color="indigo">mdi-text</v-icon>
										</v-list-tile-action>

										<v-list-tile-content>
											<div>{{ goal.description }}</div>
											<v-list-tile-sub-title>Goal Description</v-list-tile-sub-title>
										</v-list-tile-content>
									</v-list-tile>

									<v-list-tile inactive>
										<v-list-tile-action>
											<v-icon color="indigo">date_range</v-icon>
										</v-list-tile-action>

										<v-list-tile-content>
											<v-list-tile-title><AppDate :date="goal.dueDateUTC" null-date="N/A" show-date /></v-list-tile-title>
											<v-list-tile-sub-title>Due Date</v-list-tile-sub-title>
										</v-list-tile-content>
									</v-list-tile>

									<v-list-tile inactive>
										<v-list-tile-action>
											<v-icon color="indigo">person</v-icon>
										</v-list-tile-action>

										<v-list-tile-content>
											<div>
												<router-link :to="{ name: 'employee', params: { id: employeeId } }">{{ employee.fullName }}</router-link>
											</div>
											<v-list-tile-sub-title>{{ $term('employee', { case: 'title' }) }}</v-list-tile-sub-title>
										</v-list-tile-content>
									</v-list-tile>
								</v-list>
							</v-card-text>
						</v-card>
						<v-card>
							<v-toolbar flat dense>
								<v-toolbar-title class="subheading">Goal KPI's</v-toolbar-title>
							</v-toolbar>

							<v-divider></v-divider>
							<v-card-text class="pa-0">
								<employee-goal-kpi-list :kpis="goal.kpis" :can-edit="false" />
							</v-card-text>
						</v-card>
					</v-flex>

					<v-flex md6>
						<comments-list ref="commentsList" :comments="goal.comments" :loggedInEmployee="loggedInEmployee" :is-saving-comment="isSavingComment" :deleting-comment-id="deletingCommentId" @onSave="onSaveComment" @onDelete="onDeleteComment" />
					</v-flex>
				</v-layout>
			</v-container>

			<employee-goal-editor
				v-if="goalEditorDialog"
				title="Edit Goal"
				:dialog="goalEditorDialog"
				:goal="editedGoal"
				:employeeId="employeeId"
				@onSaved="onGoalSaved"
				@onDeleted="onGoalDeleted"
				@closeEditor="goalEditorDialog = false"
			></employee-goal-editor>

			<v-footer color app height="55" class="noPrint">
				<v-spacer></v-spacer>
				<footer-back-btn @click="onBackClick" />
				<v-btn @click="onEditGoal(goal)" color="primary">
					<v-icon class="mr-2">edit</v-icon>
					<span>Edit Goal</span>
				</v-btn>
			</v-footer>
		</div>
		<loading-modal v-else loading-text="Loading Goal Data..." />
	</div>
</template>

<script>
	import avatarMixin from '@/mixins/avatarMixin';
	import LoadingModal from '@/components/LoadingModal';
	import EmployeeGoalEditor from '@/components/employee/EmployeeGoalEditor';
	import CommentsList from '@/components/common/CommentsList';
	import EmployeeGoalKpiList from '@/components/employee/EmployeeGoalKpiList';
	import GoalProgress from '@/components/goals/GoalProgress';
	import goalsMixin from '@/mixins/goalsMixin';
	import textSnipMixin from '@/mixins/textSnipMixin';
	import { mapGetters } from 'vuex';

	export default {
		components: {
			CommentsList,
			EmployeeGoalKpiList,
			EmployeeGoalEditor,
			GoalProgress,
			LoadingModal
		},
		mixins: [goalsMixin, avatarMixin, textSnipMixin],
		props: {
			employeeId: {
				type: String,
				required: true
			},
			id: {
				type: String,
				required: true
			},
			goalPreloaded: {
				type: Object,
				required: false
			}
		},
		data() {
			return {
				goal: null,
				isSavingComment: false,
				deletingCommentId: null,
				snackbar: {
					show: false,
					message: '',
					color: undefined,
					buttonColor: 'pink'
				},
				goalEditorDialog: false,
				editedGoal: null,
				goalLoaded: false,
				goalLoadError: null,
				goalDeleted: false,
				employee: null
			};
		},
		created() {
			this.employee = this.$store.getters['employees/getEmployeeMini'](this.employeeId);

			if (!this.goalPreloaded) {
				this.$store.dispatch('employees/getGoal', { employeeId: this.employeeId, goalId: this.id }).then(
					(goal) => {
						this.goal = goal;
						this.$store.dispatch('setPageTitle', `Goal: ${this.$_snipText(goal.goal, 30)}`);
						this.goalLoaded = true;
					},
					(error) => {
						if (error.response.status === 404) {
							this.goalLoadError = "Goal could not be found";
						} else {
							this.goalLoadError = "An error occurred loading goal";
						}
					}
				);
			} else {
				this.goal = this.goalPreloaded;
				this.$store.dispatch('setPageTitle', `Goal: ${this.$_snipText(this.goal.goal, 30)}`);
				this.goalLoaded = true;
			}
		},
		computed: {
			...mapGetters({
				loggedInEmployee: 'loggedInEmployee'
			})
		},
		methods: {
			async onSaveComment(comment) {
				this.isSavingComment = true;

				var payload = {
					id: comment.id,
					author: comment.author,
					text: comment.text,
					employeeId: this.employeeId,
					goalId: this.id
				};

				var newComment = await this.$store.dispatch('employees/addGoalComment', payload);

				this.isSavingComment = false;
				this.$refs.commentsList.resetForm();

				if (newComment) {
					this.goal.comments.push(newComment);
					this.showSnackbar('Comment saved');
				} else {
					this.showSnackbar('Failed to save Comment', true);
				}
			},
			async onDeleteComment(comment) {
				this.deletingCommentId = comment.id;

				var payload = {
					id: comment.id,
					employeeId: this.employeeId,
					goalId: this.id
				};

				try {
					await this.$store.dispatch('employees/deleteGoalComment', payload);
					const index = this.goal.comments.findIndex((x) => x.id === comment.id);
					if (index > -1) {
						this.goal.comments.splice(index, 1);
					}

					this.deletingCommentId = null;

					this.showSnackbar('Comment deleted');
				} catch (error) {
					this.deletingCommentId = null;
					this.showSnackbar('Failed to delete Comment', true);
				}
			},
			showSnackbar(message, isError) {
				this.snackbar.show = true;
				this.snackbar.message = message;
				this.snackbar.color = isError ? 'error' : undefined;
				this.snackbar.buttonColor = isError ? '#fff' : 'pink';
				this.snackbar.timeout = isError ? 0 : 6000;
			},
			onEditGoal(item) {
				this.editedGoal = Object.assign({}, item);
				this.goalEditorDialog = true;
			},
			onGoalSaved(goal) {
				this.goal = goal;
				this.goalEditorDialog = false;
				this.$store.dispatch('setPageTitle', `Goal: ${this.$_snipText(goal.goal, 30)}`);
			},
			onGoalDeleted(id) {
				this.goalEditorDialog = false;
				this.showSnackbar('Goal deleted');
				this.goalDeleted = true;
			},
			onBackClick() {
				window.history.back();
			}
		}
	};
</script>

<style>
	.v-list.goal-detail .v-list__tile {
		height: initial !important;
		min-height: 72px;
	}
</style>
